import { useDebounce } from '@superfunnel/react-hooks/useDebounce';
import type { ExperimentStatsWithPageInfo } from '@superfunnel/types';
import Loader from '@superfunnel/ui-core/Loader';
import Button from '@superfunnel/ui-core/v2/Button';
import Input from '@superfunnel/ui-core/v2/Input';
import { classNames } from '@superfunnel/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Filter, Flask, Search } from 'iconoir-react';
import React, { useContext, useMemo, useState } from 'react';

import { UserContext } from '../../contexts/UserContext';
import ExperimentModal from '../../modals/ExperimentModal';
import { getExperimentsStats } from '../../services/DynamicPageVariantsService';
import { ExperimentRow } from './ExperimentRow';
import ExperimentsFilterPopover, {
  StatusFilter,
} from './ExperimentsFilterPopover';

// Stat card configuration
interface StatCard {
  id: string;
  label: string;
  getValue: (summary: {
    total: number;
    active: number;
    completed: number;
    readyToComplete: number;
  }) => number;
  isHighlighted?: boolean;
}

const statCards: StatCard[] = [
  {
    id: 'total',
    label: 'Total Experiments',
    getValue: (s) => s.total,
  },
  {
    id: 'active',
    label: 'Active',
    getValue: (s) => s.active,
  },
  {
    id: 'completed',
    label: 'Completed',
    getValue: (s) => s.completed,
  },
  {
    id: 'readyToComplete',
    label: 'Ready to Complete',
    getValue: (s) => s.readyToComplete,
    isHighlighted: true,
  },
];

interface ExperimentsFilters {
  status: StatusFilter;
}

interface ExperimentsHubProps {
  /** Optional dynamic page ID to filter experiments */
  dynamicPageId?: string;
  /** Optional className for the container */
  className?: string;
  /** Whether to show the header with title and stat cards. Default: true */
  showHeader?: boolean;
}

export function ExperimentsHubContent({
  dynamicPageId,
  className,
  showHeader = true,
}: ExperimentsHubProps) {
  const { selectedAccount } = useContext(UserContext);
  const queryClient = useQueryClient();

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [filters, setFilters] = useState<ExperimentsFilters>({
    status: 'all',
  });
  const [expandedExperimentIds, setExpandedExperimentIds] = useState<
    Set<string>
  >(new Set());

  // Edit experiment modal state
  const [experimentModalOpen, setExperimentModalOpen] = useState<{
    variantId: string;
    variantName: string | undefined;
    experimentId: string;
    dynamicPageId: string;
  }>();

  // Fetch experiments
  const {
    data: experimentsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['experimentsHub', selectedAccount?.id, dynamicPageId],
    queryFn: async () => {
      if (!selectedAccount?.id) return null;
      return getExperimentsStats({
        accountId: selectedAccount.id,
        dynamicPageId,
      });
    },
    enabled: !!selectedAccount?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Filter experiments based on search and status
  const filteredExperiments = useMemo(() => {
    if (!experimentsData?.experiments) return [];

    let experiments: ExperimentStatsWithPageInfo[] = Object.values(
      experimentsData.experiments,
    );

    // Apply status filter
    if (filters.status === 'active') {
      experiments = experiments.filter(
        (e) => e.experiment.status === 'running',
      );
    } else if (filters.status === 'completed') {
      experiments = experiments.filter(
        (e) => e.experiment.status === 'completed',
      );
    } else if (filters.status === 'readyToComplete') {
      experiments = experiments.filter(
        (e) => e.experiment.status === 'running' && e.status === 'winner_found',
      );
    }

    // Apply search filter
    if (debouncedSearchQuery && debouncedSearchQuery.length > 1) {
      const query = debouncedSearchQuery.toLowerCase();
      experiments = experiments.filter((e) =>
        e.name.toLowerCase().includes(query),
      );
    }

    // Sort active experiments by start date (newest first)
    experiments.sort((a, b) => {
      // Active experiments first
      if (
        a.experiment.status === 'running' &&
        b.experiment.status !== 'running'
      ) {
        return -1;
      }
      if (
        a.experiment.status !== 'running' &&
        b.experiment.status === 'running'
      ) {
        return 1;
      }
      // Then sort by start date
      const aStart =
        a.experiment.startedAt?.toDate?.() ??
        new Date(a.experiment.startedAt as any);
      const bStart =
        b.experiment.startedAt?.toDate?.() ??
        new Date(b.experiment.startedAt as any);
      return bStart.getTime() - aStart.getTime();
    });

    return experiments;
  }, [experimentsData, filters.status, debouncedSearchQuery]);

  // Check if any filter is active (not default)
  const activeFilterCount = useMemo(() => {
    return filters.status !== 'all' ? 1 : 0;
  }, [filters]);

  // Calculate summary counts
  const summary = useMemo(() => {
    return (
      experimentsData?.summary || {
        total: 0,
        active: 0,
        completed: 0,
        readyToComplete: 0,
      }
    );
  }, [experimentsData]);

  const handleToggleExpand = (experimentId: string) => {
    setExpandedExperimentIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(experimentId)) {
        newSet.delete(experimentId);
      } else {
        newSet.add(experimentId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader size={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 px-4">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900">
            Failed to load experiments
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Please try refreshing the page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('flex h-full flex-col', className)}>
      {/* Header */}

      <div className="mb-6 flex items-center justify-between gap-4">
        {showHeader ? (
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Experiments Hub
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Manage and monitor all your A/B tests
            </p>
          </div>
        ) : (
          <></>
        )}
        <div
          className={classNames(
            'flex flex-row items-center gap-2.5',
            showHeader ? '' : 'w-full',
          )}
        >
          <Input
            icon={<Search className="h-5 w-5" />}
            className="w-full sm:max-w-md"
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
            placeholder="Search experiment"
          />
          {!showHeader && <div className="flex-1"></div>}
          <div className="shrink-0">
            <ExperimentsFilterPopover
              filters={filters}
              onFiltersChange={setFilters}
              onReset={() => setFilters({ status: 'all' })}
            >
              <Button
                variant="outline"
                color="white"
                className="rounded-2lg relative h-full w-10"
              >
                {activeFilterCount > 0 && (
                  <div className="absolute top-0.5 right-0.5 rounded-md border-2 border-white bg-red-500 px-1 text-xs text-[10px] text-white">
                    {activeFilterCount}
                  </div>
                )}
                <Filter className="h-5 w-5" />
              </Button>
            </ExperimentsFilterPopover>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.id}
            className={classNames(
              'flex flex-col gap-2 rounded-xl border p-4',
              card.isHighlighted
                ? 'border-green-200 bg-green-50'
                : 'border-gray-200 bg-white',
            )}
          >
            <div className="text-sm text-gray-500">{card.label}</div>
            <div className="text-2xl font-semibold text-gray-900">
              {card.getValue(summary)}
            </div>
          </div>
        ))}
      </div>

      {/* Experiments List */}
      <div className="flex flex-1 flex-col gap-3 overflow-auto">
        {filteredExperiments.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <Flask className="h-8 w-8 text-gray-400" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900">
                No experiments found
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchQuery
                  ? 'Try adjusting your search or filters.'
                  : filters.status === 'active'
                    ? 'No active experiments. Start one from a dynamic page.'
                    : 'No experiments match the current filter.'}
              </p>
            </div>
          </div>
        ) : (
          filteredExperiments.map((experiment) => (
            <ExperimentRow
              key={experiment.experimentId}
              experiment={experiment}
              isExpanded={expandedExperimentIds.has(experiment.experimentId)}
              onToggleExpand={() => handleToggleExpand(experiment.experimentId)}
              showDynamicPageLink={!dynamicPageId}
              onEditExperiment={() =>
                setExperimentModalOpen({
                  variantId: experiment.variantId,
                  variantName: experiment.name,
                  experimentId: experiment.experimentId,
                  dynamicPageId: experiment.dynamicPageId,
                })
              }
              onExperimentCompleted={() => {
                queryClient.invalidateQueries({
                  queryKey: ['experimentsHub', selectedAccount?.id],
                });
              }}
            />
          ))
        )}
      </div>

      {/* Edit Experiment Modal */}
      <ExperimentModal
        mode="edit"
        dynamicPageId={experimentModalOpen?.dynamicPageId}
        variantId={experimentModalOpen?.variantId}
        variantName={experimentModalOpen?.variantName}
        experimentId={experimentModalOpen?.experimentId}
        open={!!experimentModalOpen}
        onClose={() => setExperimentModalOpen(undefined)}
        onSuccess={() => {
          setExperimentModalOpen(undefined);
          queryClient.invalidateQueries({
            queryKey: ['experimentsHub', selectedAccount?.id],
          });
        }}
      />
    </div>
  );
}
